import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import Button from "@/components/shared/ui/Button";

interface InvestorReportsWidgetProps {
  projectId: string;
}

export default function InvestorReportsWidget({
  projectId,
}: InvestorReportsWidgetProps) {
  const [isAvailable, setIsAvailable] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReportsAvailability() {
      try {
        const { data, error } = await supabase
          .from("projects")
          .select("investor_reports_available, name")
          .eq("id", projectId)
          .single();

        if (error) throw error;

        if (data) {
          setIsAvailable(data.investor_reports_available || false);
          setProjectName(data.name || "");
        }
      } catch (error) {
        console.error("Error fetching investor reports availability:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchReportsAvailability();
  }, [projectId]);

  const handleRequestReports = () => {
    const subject = `Investor Reports Request - ${projectName}`;
    const body = `I would like to request access to investor reports for ${projectName}.`;
    window.location.href = `mailto:hello@tcglventures.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader>
          <h3 className="text-base sm:text-lg font-bold text-black">
            Investor Reports
          </h3>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="bordered">
      <CardHeader>
        <h3 className="text-base sm:text-lg font-bold text-black">
          Investor Reports
        </h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Availability Status */}
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isAvailable ? "bg-green-500" : "bg-gray-400"
              }`}
            />
            <span className="text-sm font-medium text-gray-700">
              {isAvailable ? "Available" : "Not Available"}
            </span>
          </div>

          {/* Request Button */}
          <Button
            variant="outline"
            className="w-full text-sm"
            onClick={handleRequestReports}
          >
            Request Interest to Receive
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
